import { 
  compose, 
  not, 
  isEmpty, 
  isNil, 
  equals, 
  prop, 
  path, 
  concat, 
  contains, 
  always, 
  ifElse, 
  propIs, 
  filter, 
  flatten, 
  any, 
  hasPath, 
  includes, 
  all 
} from 'ramda';
import {
  YES_DOWNCASE,
  YES_UPPERCASE_Y,
  YES_CAPITALIZED,
  TRUE_DOWNCASE
} from 'src/services/constants';

const TRUE_UPPCASE_T = 'True';
const TRUE_CAPITALIZED = 'TRUE';
//////// LOCAL PRIVATE CONSTANTS / METHODS

const b = [
  YES_DOWNCASE,
  YES_UPPERCASE_Y,
  YES_CAPITALIZED,
  TRUE_DOWNCASE,
  TRUE_UPPCASE_T,
  TRUE_CAPITALIZED,
];

export const isEmptyOrNil = (v: any) => isEmpty(v) || isNil(v)

/* All this does is take an input and check if the value is nil (or) has value.
 *
 * Example 1
 *
 * const property = { id: "a" };
 * isNotEmpty(t) the answer is true, meaning you have value.
 *
 * Example 2
 *
 * const property = { id: "" };
 * isNotEmpty(t) the answer is false, meaning there is no value.
 */
export const isNotEmpty = compose(not, isEmptyOrNil);


export const filterIsNotEmpty = filter(isNotEmpty)


/**
is used to check given object's path value is not empty
@param path { array } keys for the path to check value
* Example 1:
* const obj1 = {'id': ""}
* const obj2 = {'id': "2"}
* const obj3 = [{'address': { "street": "Kamban Street"}}, {'address': { "street": ""}}]
* propIsNotEmpty('id')(obj1) => false
* propIsNotEmpty(['id'])(obj2) => true
TO find id is not empty
* find(propIsNotEmpty(['id']), [obj1, obj2])  => {'id': "2"}
*/
export const propIsNotEmpty = (path: string) => compose(isNotEmpty, prop(path))

export const propIsEmpty = (path: string) => compose(isEmptyOrNil, prop(path))

/**
is used to return empty string if the given value is empty value
@param v { any } value can be any data value like object, string, array if the given value is empty (like undefined/null/{}/'', []) return empty string or else return given value.
*/
export const returnStringIfNotEmpty = (v: any) => returnIsNotEmptyOrTrue(v, v, '');

/**
is used to check given object's path value is not empty
@param paths { array } keys for the path to check value
* Example 1:
* const obj1 = {'id': ""}
* const obj2 = {'id': "2"}
* const obj3 = [{'address': { "street": "Kamban Street"}}, {'address': { "street": ""}}]
* pathIsNotEmpty(['id'])(obj1) => false
* pathIsNotEmpty(['id'])(obj2) => true
TO find id is not empty
* find(pathIsNotEmpty(['id']), [obj1, obj2])  => {'id': "2"}
* find(pathIsNotEmpty(['address', 'street']), [obj1, obj2])  => {'address': { "street": "Kamban Street"}}
*/
export const pathIsNotEmpty = (paths: any) => compose(isNotEmpty, path(paths))

export const pathIsEmpty = (paths: any) => compose(isEmptyOrNil, path(paths))

export const isEmptyOrInvalid = (v: any) =>
  isEmpty(v) || isNil(v) || equals(v, 'n/a');


// if we import buildLabel from naming getting circular dependancy
const buildLabel = (key: string) => {
  return concat('lendsmart_sh', concat('/', key));
};

export const isLabelTrue = (label: string, metadata: Object) => {
  const l = buildLabel(label);
  const v = path([l], metadata);
  return ifElse(isNotEmpty, (a: any) => contains(a, b), always(false))(v);
};


/**
isNotEmptyOrTrue is used to return true if the given data is not empty
@param data { any } can be any object, string, list, boolean
*/
export const isNotEmptyOrTrue = (
  data: any,
) => {
  return ifElse(
    hasValueOrTrue,
    always(true),
    always(false)
  )((data))
}

/**
returnIsNotEmptyOrTrue is used to return trueData if the given data is not empty
@param data { any } can be any object, string, list, boolean
@param trueData { any } can be any will be return if given data is not false and not empty
@param falseData { any } can be any will be return if given data is false or empty
*/
export const returnIsNotEmptyOrTrue = (
  data: any,
  trueData: any,
  falseData?: any
) => {
  return ifElse(
    hasValueOrTrue,
    always(trueData),
    always(falseData)
  )((data))
}



/**
this method is used to return true if the given data is not empty or true incase of value is boolean

*/
export const hasValueOrTrue = (value: any) => {
  return equals(true, value) || (not(propIs(Boolean, 'value', { value })) && isNotEmpty(value))
}

type StrPath = string[] | string
type StrPaths = StrPath[]

export const hasFieldsChanged = (obj1: any, obj2: any, fields: StrPaths) => {
  return any(
    (field: StrPath) => hasPath(flatten([field]), obj1) && 
    !equals(path(flatten([field]), obj1), path(flatten([field]), obj2)),
    fields)
};


/**
 * includesList
this method is used to return true if the given 1st list items available on 2nd list it can be available all or any one of item
@param { list } items 1st list to be verified available on 2nd list 
@param { list } 2nd list which will be matched for the varification 
@param { string } logic to decide verify all items or any one of items available on 2nd list
return false if given any one of the list is empty
*/
export const includesList = (items: any[], values: any[], logic: 'any' | 'all' = 'all') => {
  if (isEmptyOrNil(items) || isEmptyOrNil(values)) {
    return false
  }
  return equals(logic, 'any') ? 
    any((item: any) => includes(item, values), items) :
    all((item: any) => includes(item, values), items)
}

/**
 * hasAnyValue
this method is used to return true if the given has any non empty values
@param { list } items 1st list to be verified 
return false if given all values are empty in that list
*/
export const hasAnyValue = (items: any[]) => {
  return isNotEmpty(items) && any(isNotEmpty, items)
}


/**
 * hasAllValue
this method is used to return true if the given has all non empty values
@param { list } items 1st list to be verified 
return false if given all values are empty in that list
*/
export const hasAllValue = (items: any[]) => {
  return isNotEmpty(items) && all(isNotEmpty, items)
}